import {
  Collapse,
  CollapseHeader,
  Flex,
  Icon,
  Text,
  useThemeColors,
  ColorsV2,
  useCollapse,
  Box,
  BoxProps,
  Skeleton,
} from '@electricjs/arc';
import { ReactNode, useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

export const StyledCollapse = styled(Collapse)<{
  borderColor: string;
  $someOptionIsChecked: boolean;
}>`
  margin-bottom: 0;
  border: ${props => (props.$someOptionIsChecked ? 'none' : 'solid')};
  border-width: ${props => (props.$someOptionIsChecked ? '0px' : '1px')};
  border-color: ${props => props.borderColor};
  border-radius: 1rem;
`;

type FilterDropdownProps = {
  id?: string;
  someOptionIsChecked: boolean;
  dropdownContent: ReactNode;
  dropdownContentProps?: BoxProps;
  filterName: string;
  size?: 'small' | 'medium';
  loading?: boolean;
};

const FilterDropdown = ({
  id,
  dropdownContent,
  dropdownContentProps,
  someOptionIsChecked,
  filterName,
  size = 'medium',
  loading = false,
}: FilterDropdownProps) => {
  const [backgroundColor, checkedBackgroundColor, checkedColor, borderColor] =
    useThemeColors([
      ColorsV2.BACKGROUND,
      ColorsV2.PRIMARY_LIGHTEST,
      ColorsV2.PRIMARY_DARK,
      ColorsV2.BORDER,
    ]);
  const { visible, setVisible } = useCollapse({
    visible: false,
  });
  const componentRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const textVariant = size === 'small' ? 'body-2' : 'body';
  const textVariantChecked = size === 'small' ? 'label-small' : 'label-large';

  // Calculate dropdown position
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });

  const updateDropdownPosition = useCallback(() => {
    if (componentRef.current && visible) {
      const rect = componentRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 16, // 16px margin top
        left: rect.left,
        width: rect.width < 200 ? 200 : rect.width, // min width 200px
      });
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      updateDropdownPosition();
      // Listen for scroll events with capture to catch all scroll events
      // This prevents dropdowns from having a fixed position on the page when scrolling
      window.addEventListener('scroll', updateDropdownPosition, {
        capture: true,
        passive: true,
      });
      window.addEventListener('resize', updateDropdownPosition);

      document.addEventListener('scroll', updateDropdownPosition, {
        capture: true,
        passive: true,
      });
    }

    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, {
        capture: true,
      });
      window.removeEventListener('resize', updateDropdownPosition);
      document.removeEventListener('scroll', updateDropdownPosition, {
        capture: true,
      });
    };
  }, [visible, updateDropdownPosition]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        !event.target ||
        (componentRef.current &&
          !componentRef.current.contains(event.target as HTMLDivElement) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as HTMLDivElement))
      ) {
        setVisible(false);
      }
    };
    document.addEventListener('click', handleOutsideClick, { capture: true });
    return () => {
      document.removeEventListener('click', handleOutsideClick, {
        capture: true,
      });
    };
  }, [componentRef, setVisible]);

  const headerVerticalPadding = someOptionIsChecked
    ? size === 'small'
      ? '0.7rem'
      : '0.5rem'
    : size === 'small'
      ? '0.6rem'
      : '0.8rem';

  const dropdownContentElement = visible ? (
    <Box
      ref={dropdownRef}
      position="fixed"
      zIndex={10000}
      backgroundColor={backgroundColor}
      borderRadius="1rem"
      padding="1rem"
      width={`${dropdownPosition.width}px`}
      boxShadow={`0 0 0.5rem ${borderColor}`}
      style={{
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
      }}
      {...dropdownContentProps}>
      {dropdownContent}
    </Box>
  ) : null;

  if (loading) {
    return (
      <Box>
        {/* Just a fake filter, showed as closed and loading */}
        <StyledCollapse
          contained
          $someOptionIsChecked={false}
          borderColor={borderColor}
          visible={false}
          disabled>
          <CollapseHeader minHeight="unset" borderRadius="1rem" py="0.8rem">
            <Box width="4rem">
              <Skeleton />
            </Box>
          </CollapseHeader>
        </StyledCollapse>
      </Box>
    );
  }

  return (
    <Box id={id} ref={componentRef} position="relative">
      <StyledCollapse
        contained
        borderColor={borderColor}
        onToggle={() => setVisible(e => !e)}
        $someOptionIsChecked={someOptionIsChecked}
        visible={visible}>
        <CollapseHeader
          {...(someOptionIsChecked && {
            backgroundColor: checkedBackgroundColor,
          })}
          minHeight="unset"
          borderRadius="1rem"
          py={headerVerticalPadding}
          borderBottom="none">
          <Flex alignItems="center">
            {someOptionIsChecked && (
              <Icon icon="check" color={checkedColor} size="small" mr="1rem" />
            )}
            <Text
              variant={textVariant}
              {...(someOptionIsChecked && { color: checkedColor })}
              {...(someOptionIsChecked && { variant: textVariantChecked })}
              {...(!someOptionIsChecked && { lineHeight: '1.8rem' })}>
              {filterName}
            </Text>
          </Flex>
        </CollapseHeader>
      </StyledCollapse>

      {/* Portal the dropdown content to document.body to avoid clipping */}
      {dropdownContentElement &&
        createPortal(dropdownContentElement, document.body)}
    </Box>
  );
};

export default FilterDropdown;
